import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { cn } from '@/lib/utils';
import '@/styles/about-responsive.css';

interface StoryStep {
  number: string;
  title: string;
  subtitle: string;
  text: string;
  image: string;
  cam: string;
}

interface FactoryStorytellingProps {
  steps: StoryStep[];
  heroEyebrow?: string;
  heroTitle?: string;
  heroSubtitle?: string;
}

export const FactoryStorytelling: React.FC<FactoryStorytellingProps> = ({ steps, heroEyebrow, heroTitle, heroSubtitle }) => {
  const { t } = useLanguage();
  const [activeMobileId, setActiveMobileId] = useState<string | null>(null);

  const toggleMobileActive = (id: string) => {
    setActiveMobileId(prev => prev === id ? null : id);
  };

  // headerVariants — left-to-right entrance, matching /news page behaviour
  const headerVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  return (
    <div className="relative w-full bg-background z-10">
      <div className="grid-cells-accent opacity-10" />

      {/* Hero Header */}
      <div className="relative z-10">
        <section className="pt-24 md:pt-32 pb-0 lg:pb-16 relative">
          <div className="grid-cells opacity-50" />
          <div className="container-custom relative z-10">
            <div className="space-y-4 lg:space-y-6">
              {heroEyebrow?.trim() && (
                <motion.div
                  custom={0}
                  initial={false}
                  animate="visible"
                  variants={headerVariants}
                  className="flex items-center gap-2 lg:gap-3 about-header-hide-small"
                >
                  <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-[#B08A5A] shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                  <p className="text-[#B08A5A] font-black uppercase tracking-[0.4em] text-[10px]">
                    {heroEyebrow}
                  </p>
                </motion.div>
              )}
              {heroTitle?.trim() && (
                <motion.h1
                  custom={1}
                  initial={false}
                  animate="visible"
                  variants={headerVariants}
                  className="text-white font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8] about-header-hide-small"
                >
                  {heroTitle}<span className="text-[#B08A5A]">.</span>
                </motion.h1>
              )}
              {heroSubtitle?.trim() && (
                <motion.p
                  custom={2}
                  initial={false}
                  animate="visible"
                  variants={headerVariants}
                  className="text-white/40 max-w-xl text-sm md:text-base lg:text-lg leading-relaxed font-black uppercase tracking-wider about-header-hide-small"
                >
                  {heroSubtitle}
                </motion.p>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Steps — vertical on all screens, alternating layout on desktop */}
      <div className="flex flex-col w-full">
        {steps.map((step, idx) => {
          // Even idx (0,2) → text left, image right; Odd idx (1,3) → image left, text right
          const isReversed = idx % 2 === 1;

          return (
            <div
              key={step.number}
              className="w-full relative flex items-center overflow-hidden py-8 lg:py-24 xl:py-32 about-storytelling-block"
            >
              {/* Background Number — behind everything */}
              <div className="absolute inset-0 flex items-center pointer-events-none select-none z-0 overflow-hidden">
                <span
                  className={`font-black leading-none text-[#B08A5A]/5 tracking-tighter about-bg-number${idx % 2 === 0 ? ' about-bg-number-shift-down' : ''}`}
                  style={{
                    fontSize: 'clamp(14rem, 22vw, 36rem)',
                    position: 'absolute',
                    left: isReversed ? 'auto' : '2%',
                    right: isReversed ? '2%' : 'auto',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    lineHeight: 1,
                  }}
                >
                  {step.number}
                </span>
              </div>

              {/* Content Grid — relative z-10 to stay above number */}
              <div
                className={cn(
                  'container-custom relative z-10 w-full grid grid-cols-1 items-center about-content-grid',
                  'lg:grid-cols-2',
                  isReversed ? 'lg:direction-rtl' : ''
                )}
                style={{ gap: 'clamp(2rem, 4vw, 6rem)' }}
              >
                {/* Text Block */}
                <div
                  className={cn(
                    'flex flex-col min-w-0',
                    isReversed ? 'lg:order-2' : 'lg:order-1',
                    idx === 0 ? 'about-first-content-block' : ''
                  )}
                  style={{
                    gap: 'clamp(1rem, 1.6vw, 3rem)',
                    paddingTop: idx === 0 ? 'clamp(0rem, 4vh, 4rem)' : '0',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(0.65rem, 1vw, 1.5rem)' }}>
                    <div
                      className="text-[#B08A5A] uppercase tracking-[0.5em] font-bold about-subtitle-text"
                      style={{ fontSize: 'clamp(9px, 0.9vw, 16px)' }}
                    >
                      {step.subtitle}
                    </div>
                    <h2
                      className="font-black uppercase tracking-tighter text-white whitespace-pre-line about-main-title"
                      style={{ fontSize: 'clamp(1.6rem, 3vw, 5rem)', lineHeight: '0.9' }}
                    >
                      {step.title}
                    </h2>
                  </div>

                  <p
                    className="text-white/40 font-medium about-description-text"
                    style={{ fontSize: 'clamp(13px, 1vw, 18px)', lineHeight: '1.5', maxWidth: '52ch' }}
                  >
                    {step.text}
                  </p>
                </div>

                {/* Image Block */}
                <div
                  className={cn(
                    'relative w-full min-w-0 about-image-container',
                    isReversed ? 'lg:order-1' : 'lg:order-2'
                  )}
                  style={{ aspectRatio: '1 / 1' }}
                >
                  <div
                    className={cn(
                      'relative w-full h-full story-image-wrapper overflow-hidden rounded-sm border border-white/5 shadow-2xl group cursor-pointer bg-[#13100B]',
                      activeMobileId === step.number && 'is-active'
                    )}
                    onClick={() => toggleMobileActive(step.number)}
                  >
                    {/* Shine Effect */}
                    <div className="shine-overlay" />

                    <img
                      src={step.image}
                      alt={step.title}
                      className={cn(
                        'story-image w-full h-full object-cover object-center will-change-transform relative z-0',
                        'grayscale brightness-[0.75] contrast-[1.05] transform-gpu scale-[1.005]',
                        'lg:group-hover:grayscale-0 lg:group-hover:brightness-100 lg:group-hover:contrast-[1.08] lg:group-hover:saturate-[1.1] lg:group-hover:scale-105',
                        activeMobileId === step.number && 'grayscale-0 brightness-100 contrast-[1.08] saturate-[1.1] scale-105',
                        'motion-reduce:transition-none'
                      )}
                      style={{
                        transition: 'transform 2000ms ease-in-out, filter 1000ms ease-in-out, opacity 1000ms ease-in-out'
                      }}
                    />

                    {/* HUD Overlay */}
                    <div
                      className="absolute inset-0 flex flex-col justify-between pointer-events-none z-30 about-hud-overlay"
                      style={{ padding: 'clamp(0.75rem, 2vw, 3rem)' }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center about-hud-gap" style={{ gap: 'clamp(0.5rem, 1vw, 1rem)' }}>
                          <div
                            className="rounded-full bg-red-600 animate-pulse shadow-[0_0_20px_rgba(220,38,38,0.6)] about-hud-dot"
                            style={{ width: 'clamp(0.5rem, 0.9vw, 0.875rem)', height: 'clamp(0.5rem, 0.9vw, 0.875rem)' }}
                          ></div>
                          <div
                            className="font-mono text-white/40 uppercase tracking-[0.3em] font-bold about-hud-text"
                            style={{ fontSize: 'clamp(8px, 0.8vw, 14px)' }}
                          >
                            {t('about.cam_hud.system_active')}
                          </div>
                        </div>
                        <div
                          className="font-mono text-white/40 uppercase tracking-[0.3em] font-bold about-hud-text"
                          style={{ fontSize: 'clamp(8px, 0.8vw, 14px)' }}
                        >
                          {t('about.cam')}: {step.cam}
                        </div>
                      </div>
                      <div className="flex justify-between items-end opacity-30">
                        <div
                          className="border-l-2 border-b-2 border-[#B08A5A] about-hud-bracket"
                          style={{ width: 'clamp(1.5rem, 2.5vw, 3.5rem)', height: 'clamp(1.5rem, 2.5vw, 3.5rem)' }}
                        ></div>
                        <div
                          className="border-r-2 border-b-2 border-[#B08A5A] about-hud-bracket"
                          style={{ width: 'clamp(1.5rem, 2.5vw, 3.5rem)', height: 'clamp(1.5rem, 2.5vw, 3.5rem)' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
